import React from 'react';
import { Star, Moon, Sun, Heart, Briefcase, Home, Users, Gem, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLang } from '../context/LanguageContext';

const services = [
  { icon: Star, title: 'Kundli Analysis', titleHi: 'कुंडली विश्लेषण', desc: 'Detailed birth chart with Dasha & Yoga predictions', descHi: 'दशा एवं योग के साथ विस्तृत जन्म कुंडली', span: 'md:col-span-2', gradient: 'from-gold/20 to-gold-dark/10' },
  { icon: Moon, title: 'Horoscope', titleHi: 'राशिफल', desc: 'Daily, weekly & yearly zodiac predictions', descHi: 'दैनिक, साप्ताहिक एवं वार्षिक राशिफल', span: '', gradient: 'from-purple-light/20 to-purple/10' },
  { icon: Heart, title: 'Kundli Matching', titleHi: 'कुंडली मिलान', desc: 'Marriage compatibility & Gun Milan', descHi: 'वैवाहिक अनुकूलता एवं गुण मिलान', span: '', gradient: 'from-pink-500/20 to-rose-500/10' },
  { icon: Sun, title: 'Grah Shanti', titleHi: 'ग्रह शांति पूजा', desc: 'Planetary dosha remedies & Shanti Puja', descHi: 'ग्रह दोष निवारण एवं शांति पूजा', span: 'md:col-span-2', gradient: 'from-amber-500/20 to-orange-500/10' },
  { icon: Home, title: 'Vastu Shastra', titleHi: 'वास्तु शास्त्र', desc: 'Home & office Vastu for prosperity', descHi: 'घर एवं ऑफिस वास्तु समृद्धि के लिए', span: '', gradient: 'from-emerald-500/20 to-green-600/10' },
  { icon: Briefcase, title: 'Career Guidance', titleHi: 'करियर मार्गदर्शन', desc: 'Professional success & growth', descHi: 'व्यावसायिक सफलता एवं विकास', span: '', gradient: 'from-blue-500/20 to-indigo-500/10' },
  { icon: Users, title: 'Family Solutions', titleHi: 'पारिवारिक समस्या', desc: 'Resolve disputes & bring harmony', descHi: 'विवाद सुलझाएं, सद्भाव लाएं', span: '', gradient: 'from-cyan-500/20 to-teal-500/10' },
  { icon: Gem, title: 'Gemstone', titleHi: 'रत्न परामर्श', desc: 'Lucky gemstone for prosperity & health', descHi: 'समृद्धि एवं स्वास्थ्य के लिए शुभ रत्न', span: '', gradient: 'from-red-500/20 to-pink-500/10' },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function Services() {
  const { lang, t } = useLang();
  return (
    <section id="services" className="section-padding relative overflow-hidden" data-testid="services-section">
      <div className="absolute inset-0 cosmic-bg-alt" />
      <div className="absolute top-1/4 left-0 w-80 h-80 bg-purple/8 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-gold/5 rounded-full blur-[100px]" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body mb-4">
            {t('services.tag')}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mb-6 tracking-tight">
            {t('services.title1')} <span className="text-gold-gradient">{t('services.title2')}</span>
          </h2>
          <p className="text-text-muted">
            {t('services.desc')}
          </p>
          <div className="divine-divider mt-8 max-w-xs mx-auto" />
        </div>

        <motion.div className="grid md:grid-cols-4 gap-4" variants={container} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
          {services.map((s, i) => (
            <motion.div key={i} variants={item}
              className={`glass-card service-card p-6 group cursor-pointer ${s.span}`}
              data-testid={`service-card-${i}`}>
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${s.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform border border-white/5`}>
                <s.icon className="w-7 h-7 text-gold" />
              </div>
              <h3 className="font-heading text-xl text-text-main mb-1">{lang === 'hi' ? s.titleHi : s.title}</h3>
              <p className="text-gold/60 text-sm mb-2 font-heading italic">{lang === 'hi' ? s.title : s.titleHi}</p>
              <p className="text-text-muted text-sm">{lang === 'hi' ? s.descHi : s.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="text-center mt-12">
          <a href="#book-consultation" className="btn-gold inline-flex items-center gap-2" data-testid="services-cta-btn">
            Book Consultation <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
